import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/ui/panel";
import { PageSkeleton } from "@/components/ui/skeleton";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { api } from "@/lib/api";
import {
  FilterField,
  FilterToolbar,
  OverriddenBadge,
  PaginationBar,
  SearchInput,
  billingStatusTone,
  formatUnixDate,
  fromDatetimeLocalValue,
  selectClassName,
  toDatetimeLocalValue,
} from "./shared";
import { StatusBadge } from "@/components/status/status-badge";

type UsageAccount = {
  accountId: string;
  name: string;
  accountType: string;
  billingStatus: string | null;
  quotas: {
    planDefaults: {
      maxConferences: number;
      maxStreams: number;
      maxRunsPerDay: number;
    };
    overrides: {
      maxConferences: number | null;
      maxStreams: number | null;
      maxRunsPerDay: number | null;
      attendeesPerStream: number | null;
      expiresAt: number | null;
      note: string | null;
      permanent: boolean;
    } | null;
    effective: {
      maxConferences: number;
      maxStreams: number;
      maxRunsPerDay: number;
      attendeesPerStream: number | null;
    };
    planCode: string;
    isOverridden: boolean;
  };
  usage: {
    conferences: number;
    streams: number;
    activeStreams: number;
    runsToday: number;
    attendeesThisPeriod: number;
    trialAttendeesUsed: number;
  };
};

export function SuperAdminUsagePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const focusAccountId = searchParams.get("accountId") ?? "";
  const [qDraft, setQDraft] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const [accounts, setAccounts] = useState<UsageAccount[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editAccount, setEditAccount] = useState<UsageAccount | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const [maxConferences, setMaxConferences] = useState("");
  const [maxStreams, setMaxStreams] = useState("");
  const [maxRunsPerDay, setMaxRunsPerDay] = useState("");
  const [attendeesPerStream, setAttendeesPerStream] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [note, setNote] = useState("");
  const [permanent, setPermanent] = useState(false);
  const [clearOverrides, setClearOverrides] = useState(false);

  async function refresh() {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    if (q) params.set("q", q);
    const r = await api<{
      accounts: UsageAccount[];
      pagination: { total: number };
    }>(`/api/platform/usage?${params}`);
    setAccounts(r.accounts ?? []);
    setTotal(r.pagination?.total ?? 0);
    if (focusAccountId) {
      const focused = (r.accounts ?? []).find(
        (a) => a.accountId === focusAccountId,
      );
      if (focused) openEditor(focused);
    }
  }

  useEffect(() => {
    setLoading(true);
    void refresh()
      .catch(() => {
        setAccounts([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, offset]);

  function openEditor(account: UsageAccount) {
    setEditAccount(account);
    setClearOverrides(false);
    const o = account.quotas.overrides;
    setMaxConferences(
      o?.maxConferences != null ? String(o.maxConferences) : "",
    );
    setMaxStreams(o?.maxStreams != null ? String(o.maxStreams) : "");
    setMaxRunsPerDay(o?.maxRunsPerDay != null ? String(o.maxRunsPerDay) : "");
    setAttendeesPerStream(
      o?.attendeesPerStream != null ? String(o.attendeesPerStream) : "",
    );
    setExpiresAt(toDatetimeLocalValue(o?.expiresAt ?? null));
    setNote(o?.note ?? "");
    setPermanent(Boolean(o?.permanent));
  }

  const page = Math.floor(offset / limit) + 1;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-rs-ink">Usage / Quotas</h1>
        <p className="text-sm text-rs-ink-muted">
          Effective limits vs plan defaults. Overridden accounts show an
          Overridden badge.
        </p>
      </div>
      <FilterToolbar>
        <FilterField
          id="sa-usage-q"
          label="Search"
          className="min-w-[14rem] flex-1 space-y-1"
        >
          <SearchInput
            id="sa-usage-q"
            value={qDraft}
            onChange={setQDraft}
            placeholder="Organization name or id"
          />
        </FilterField>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setQ(qDraft.trim());
            setOffset(0);
          }}
        >
          Search
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setQDraft("");
            setQ("");
            setOffset(0);
            setSearchParams({});
          }}
        >
          Clear
        </Button>
      </FilterToolbar>

      {loading ? (
        <PageSkeleton />
      ) : (
        <>
          <Panel>
            <ul className="divide-y divide-rs-border">
              {accounts.map((a) => (
                <li key={a.accountId} className="px-5 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          className="font-medium text-rs-accent hover:underline"
                          to={`/superadmin/accounts/${a.accountId}`}
                        >
                          {a.name}
                        </Link>
                        <StatusBadge tone={billingStatusTone(a.billingStatus)}>
                          {a.billingStatus ?? "none"}
                        </StatusBadge>
                        <OverriddenBadge show={a.quotas.isOverridden} />
                      </div>
                      <p className="mt-1 text-sm text-rs-ink-muted">
                        Plan {a.quotas.planCode} · Streams{" "}
                        {a.usage.activeStreams}/{a.quotas.effective.maxStreams}{" "}
                        active · Runs today {a.usage.runsToday}/
                        {a.quotas.effective.maxRunsPerDay} · Attendees{" "}
                        {a.usage.attendeesThisPeriod.toLocaleString()}
                      </p>
                      <dl className="mt-2 grid gap-1 text-xs text-rs-ink-subtle sm:grid-cols-2 lg:grid-cols-4">
                        <div>
                          Conferences: {a.usage.conferences}/
                          {a.quotas.effective.maxConferences}
                          {a.quotas.isOverridden &&
                          a.quotas.effective.maxConferences !==
                            a.quotas.planDefaults.maxConferences
                            ? ` (plan ${a.quotas.planDefaults.maxConferences})`
                            : ""}
                        </div>
                        <div>
                          Streams: {a.usage.streams}/
                          {a.quotas.effective.maxStreams}
                          {a.quotas.isOverridden &&
                          a.quotas.effective.maxStreams !==
                            a.quotas.planDefaults.maxStreams
                            ? ` (plan ${a.quotas.planDefaults.maxStreams})`
                            : ""}
                        </div>
                        <div>
                          Runs/day: {a.usage.runsToday}/
                          {a.quotas.effective.maxRunsPerDay}
                        </div>
                        <div>
                          Attendees/stream:{" "}
                          {a.quotas.effective.attendeesPerStream ?? "default"}
                        </div>
                      </dl>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => openEditor(a)}
                    >
                      Edit Quotas
                    </Button>
                  </div>
                </li>
              ))}
              {accounts.length === 0 ? (
                <li className="px-5 py-8 text-center text-sm text-rs-ink-muted">
                  No accounts found.
                </li>
              ) : null}
            </ul>
          </Panel>
          <PaginationBar
            page={page}
            limit={limit}
            total={total}
            onPageChange={(p) => setOffset((p - 1) * limit)}
          />
        </>
      )}

      {message ? <p className="text-sm text-rs-ink-muted">{message}</p> : null}

      <ConfirmDialog
        open={editAccount != null}
        onOpenChange={(open) => {
          if (!open) setEditAccount(null);
        }}
        title="Update Quota Overrides"
        description={
          editAccount
            ? `Set or clear Super Admin overrides for ${editAccount.name}. Effective limits replace plan defaults while active.`
            : ""
        }
        impact={
          editAccount ? (
            <div className="space-y-2">
              <p className="text-xs text-rs-ink-muted">
                Plan defaults: conf{" "}
                {editAccount.quotas.planDefaults.maxConferences}, streams{" "}
                {editAccount.quotas.planDefaults.maxStreams}, runs{" "}
                {editAccount.quotas.planDefaults.maxRunsPerDay}
              </p>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={clearOverrides}
                  onChange={(e) => setClearOverrides(e.target.checked)}
                />
                Clear All Overrides
              </label>
              {!clearOverrides ? (
                <>
                  <Input
                    type="number"
                    value={maxConferences}
                    onChange={(e) => setMaxConferences(e.target.value)}
                    placeholder="Max Conferences Override"
                    aria-label="Max Conferences Override"
                  />
                  <Input
                    type="number"
                    value={maxStreams}
                    onChange={(e) => setMaxStreams(e.target.value)}
                    placeholder="Max Streams Override"
                    aria-label="Max Streams Override"
                  />
                  <Input
                    type="number"
                    value={maxRunsPerDay}
                    onChange={(e) => setMaxRunsPerDay(e.target.value)}
                    placeholder="Max Runs Per Day Override"
                    aria-label="Max Runs Per Day Override"
                  />
                  <Input
                    type="number"
                    value={attendeesPerStream}
                    onChange={(e) => setAttendeesPerStream(e.target.value)}
                    placeholder="Attendees Per Stream Override"
                    aria-label="Attendees Per Stream Override"
                  />
                  <Input
                    type="datetime-local"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    aria-label="Override Expires At"
                  />
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={permanent}
                      onChange={(e) => setPermanent(e.target.checked)}
                    />
                    Permanent Override
                  </label>
                  <Input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Note"
                    aria-label="Override Note"
                  />
                </>
              ) : null}
              {editAccount.quotas.overrides?.expiresAt ? (
                <p className="text-xs text-rs-ink-muted">
                  Current expiry:{" "}
                  {formatUnixDate(editAccount.quotas.overrides.expiresAt)}
                </p>
              ) : null}
            </div>
          ) : null
        }
        confirmLabel={clearOverrides ? "Clear Overrides" : "Save Overrides"}
        dangerous={clearOverrides}
        busy={busy}
        onConfirm={async () => {
          if (!editAccount) return;
          setBusy(true);
          try {
            const parseOpt = (v: string) => {
              const t = v.trim();
              if (!t) return null;
              const n = Number(t);
              return Number.isFinite(n) ? Math.trunc(n) : null;
            };
            await api(
              `/api/platform/accounts/${encodeURIComponent(editAccount.accountId)}/quotas`,
              {
                method: "PUT",
                body: JSON.stringify(
                  clearOverrides
                    ? { clear: true }
                    : {
                        maxConferences: parseOpt(maxConferences),
                        maxStreams: parseOpt(maxStreams),
                        maxRunsPerDay: parseOpt(maxRunsPerDay),
                        attendeesPerStream: parseOpt(attendeesPerStream),
                        expiresAt: permanent
                          ? null
                          : fromDatetimeLocalValue(expiresAt),
                        note: note.trim() || null,
                        permanent,
                      },
                ),
              },
            );
            setEditAccount(null);
            setMessage("Quotas updated.");
            setSearchParams({});
            await refresh();
          } catch (err) {
            setMessage(
              err instanceof Error ? err.message : "Quota update failed",
            );
          } finally {
            setBusy(false);
          }
        }}
      />
    </div>
  );
}
